import { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'
import useApiHook from '../../hooks/useApiHook'
import useGetApiKeys from '../../hooks/useGetApiKeys'

import SelectBox from '../../components/SelectBox'
import Inputs from './inputs'
import History from './history'

import { encodeApiKey } from '../../utils/api-key-encoding'
import GuidedTour from '../../components/GuidedTour'

const ADMIN_ENVS = ['dev', 'uat', 'prod']

const ApiBrowser = ({ role, customer }) => {
  const [customerKeyValue, setCustomerKeyValue] = useState(null)
  const [customerKeyName, setCustomerKeyName] = useState(null)

  const isCustomer = role === 'customer'
  const isAdmin = role === 'admin'
  const defaultEnv = 'uat'

  // Admin: fetch all API keys
  const { keys: adminKeys, loading: adminKeysLoading } = useGetApiKeys(isCustomer)
  const [selectedAdminKeyId, setSelectedAdminKeyId] = useState('')
  const [selectedAdminKey, setSelectedAdminKey] = useState(null)

  // For customers, fetch their keys and find the UAT one
  useEffect(() => {
    if (!isCustomer || !customer?.apiKeyIds?.length) return

    const fetchKeys = async () => {
      try {
        const getCustomerKeys = httpsCallable(getFunctions(), 'getCustomerKeys')
        const res = await getCustomerKeys({ apiKeyIds: customer.apiKeyIds })
        const keys = res.data.keys || []
        const uatKey = keys.find((k) => k.name.toLowerCase().includes('uat'))
        const selectedKey = uatKey || keys[0]
        if (selectedKey) {
          setCustomerKeyValue(selectedKey.value)
          setCustomerKeyName(selectedKey.name)
        }
      } catch (err) {
        console.error('Error fetching customer keys for API Browser:', err)
      }
    }
    fetchKeys()
  }, [isCustomer, customer])

  const encodedKey = isCustomer
    ? (customerKeyValue ? encodeApiKey(customerKeyValue) : undefined)
    : (selectedAdminKey ? encodeApiKey(selectedAdminKey) : undefined)

  const { activeSession, selectSession, createSession, callApi, isApiCalling, env, setEnv, setApiKey } = useApiHook(defaultEnv, encodedKey)

  // Update the hook's key when admin selection changes
  useEffect(() => {
    if (isAdmin && selectedAdminKey) {
      setApiKey(encodeApiKey(selectedAdminKey))
    }
  }, [selectedAdminKey])

  const handleAdminKeyChange = (e) => {
    const keyId = e.target.value
    setSelectedAdminKeyId(keyId)
    const keyObj = adminKeys.find((k) => k.id === keyId)
    setSelectedAdminKey(keyObj?.value || null)
  }

  useEffect(() => {
    createSession({ id: 'DEFAULT', name: 'DEFAULT' })
    selectSession('DEFAULT')
  }, [])

  return (
    <div className='py-2 text-blue-10 p-2'>
      <GuidedTour
        tourKey='api_browser'
        steps={[
          { target: '[data-tour="api-inputs"]', title: 'Medipro Browser', content: 'Configure your API request parameters here — select endpoints, set inputs, and submit calls.' },
          { target: '[data-tour="section-contact"]', title: 'Start with a Contact', content: 'First create a Contact — many other API calls require one. Refresh the page to start over.', onEnter: () => { const btn = document.querySelector('[data-tour="section-contact"] button'); if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click() } },
          { target: '[data-tour="section-health"]', title: 'Health & Medications', content: 'To get Predict Plans, you must PUT health conditions and medications — even if they are empty.', onEnter: () => { const btn = document.querySelector('[data-tour="section-health"] button'); if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click() } },
          { target: '[data-tour="section-preferences"]', title: 'Plan Type', content: 'The Plan Type can be changed in Preferences before running Predict Plans.', onEnter: () => { const btn = document.querySelector('[data-tour="section-preferences"] button'); if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click() } },
          { target: '[data-tour="section-predict-plans"]', title: 'Async Results', content: 'Predict Plans are asynchronous, so multiple GET requests may be required to get Plan Results.', onEnter: () => { const btn = document.querySelector('[data-tour="section-predict-plans"] button'); if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click() } },
          { target: '[data-tour="api-history-header"]', title: 'History', content: 'View the results of your API calls. Each call is logged with its response.' }
        ]}
      />
      <div className='flex flex-row items-center justify-between'>
        <div>
          <h1 className='text-4xl'>Medipro Browser</h1>
          <a href='https://docs.aipredict.medipro.inspireht.com/' target='_blank' rel='noopener noreferrer' className='text-sm text-blue-6 hover:underline'>
            Medipro API Documentation
          </a>
        </div>
      </div>
      {isCustomer && (
        <div className='text-sm text-gray-600 mb-2'>
          Environment: UAT (restricted) | API Key: {customerKeyName || 'Loading...'}
        </div>
      )}
      {isAdmin && (
        <div className='flex items-end gap-3 mb-3' data-tour='api-admin-controls'>
          <SelectBox
            containerClassName='mb-0'
            label='Environment'
            name='adminEnv'
            value={env}
            onChange={(e) => setEnv(e.target.value)}
          >
            {ADMIN_ENVS.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </SelectBox>
          <SelectBox
            containerClassName='mb-0'
            label='API Key'
            name='adminKey'
            value={selectedAdminKeyId}
            onChange={handleAdminKeyChange}
          >
            <option value=''>-- Select Key --</option>
            {!adminKeysLoading && adminKeys
              .filter((k) => k.id !== 'all')
              .map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
          </SelectBox>
        </div>
      )}
      <div className='flex flex-row gap-4'>
        <div data-tour='api-inputs' className='flex-1'>
          <Inputs activeSession={activeSession} callApiFn={callApi} isApiCalling={isApiCalling} />
        </div>
        <div data-tour='api-history' className='flex-1'>
          <History history={activeSession?.history} providers={activeSession?.providers} isApiCalling={isApiCalling} />
        </div>
      </div>
    </div>
  )
}

export default ApiBrowser
